import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const DRAFT_KEY = "simpler.novoContrato.rascunho";

const PAYMENT_METHODS = [
  "Boleto bancário",
  "PIX",
  "Cartão de crédito",
  "Débito automático",
];

const STEPS = [
  { id: 1, title: "Empresa", hint: "CNPJ e dados cadastrais" },
  { id: 2, title: "Contatos", hint: "Quem assina e quem acompanha" },
  { id: 3, title: "Condições", hint: "Valores e pagamento" },
  { id: 4, title: "Revisão", hint: "Conferir e enviar" },
];

const EMPTY = {
  customer_cnpj: "",
  customer_name: "",
  customer_address: "",
  responsible_name: "",
  responsible_email: "",
  responsible_phone: "",
  accounting_contact_name: "",
  accounting_contact_email: "",
  accounting_contact_phone: "",
  fiscal_contact_name: "",
  fiscal_contact_email: "",
  fiscal_contact_phone: "",
  companies_limit: "",
  setup_fee: "",
  monthly_fee: "",
  start_date: "",
  due_day: "10",
  payment_method: "Boleto bancário",
  setup_installments: "1",
  first_monthly_date: "",
  consultant_id: "",
  notes: "",
};

const onlyDigits = (v) => String(v || "").replace(/\D/g, "");

const fmtCnpj = (v) => {
  const d = onlyDigits(v).slice(0, 14);
  let o = d.slice(0, 2);
  if (d.length > 2) o += "." + d.slice(2, 5);
  if (d.length > 5) o += "." + d.slice(5, 8);
  if (d.length > 8) o += "/" + d.slice(8, 12);
  if (d.length > 12) o += "-" + d.slice(12);
  return o;
};

const fmtPhone = (v) => {
  const d = onlyDigits(v).slice(0, 11);
  if (d.length <= 2) return d;
  if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  if (d.length <= 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
};

const fmtMoney = (v) => {
  const d = onlyDigits(v);
  if (!d) return "";
  return (parseInt(d, 10) / 100).toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const toCents = (v) => parseInt(onlyDigits(v) || "0", 10);

const centsToInput = (c) =>
  c
    ? (c / 100).toLocaleString("pt-BR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    : "";

const isEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(v || "").trim());

const fmtDate = (v) => {
  if (!v) return "—";
  const [y, m, d] = String(v).slice(0, 10).split("-");
  return d && m && y ? `${d}/${m}/${y}` : v;
};

const inputCls =
  "w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-[#101652] outline-none transition-all placeholder:text-slate-300 focus:border-[#5b3bf0] focus:ring-4 focus:ring-[#5b3bf0]/10 disabled:bg-slate-50 disabled:text-slate-400";

function Field({ label, hint, required, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="mb-2 block text-[11px] font-bold uppercase tracking-[0.14em] text-slate-500">
        {label}
        {required && <span className="ml-1 text-[#5b3bf0]">*</span>}
      </span>
      {children}
      {hint && <span className="mt-1.5 block text-[13px] text-slate-400">{hint}</span>}
    </label>
  );
}

function Section({ title, description, children }) {
  return (
    <div className="rounded-3xl border border-slate-200/80 bg-slate-50/50 p-5">
      <div className="mb-4">
        <div className="text-sm font-bold text-[#101652]">{title}</div>
        {description && <div className="mt-0.5 text-[13px] text-slate-500">{description}</div>}
      </div>
      <div className="grid gap-4 md:grid-cols-3">{children}</div>
    </div>
  );
}

function ReviewBlock({ title, onEdit, rows }) {
  return (
    <div className="rounded-3xl border border-slate-200/80 bg-white p-5">
      <div className="mb-3 flex items-center justify-between">
        <div className="text-sm font-bold text-[#101652]">{title}</div>
        <button type="button" onClick={onEdit} className="text-[13px] font-bold text-[#5b3bf0] hover:underline">
          Editar
        </button>
      </div>
      <dl className="grid gap-x-6 gap-y-2 sm:grid-cols-2">
        {rows.map(([k, v]) => (
          <div key={k} className="min-w-0">
            <dt className="text-[12px] font-bold uppercase tracking-[0.14em] text-slate-400">{k}</dt>
            <dd className="truncate text-sm text-[#101652]">{v || "—"}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}


function ContractDoneScreen({ done, form, consultantName, nav }) {
  const [copied, setCopied] = useState(false);
  const sent = Boolean(done?.sent);
  const first = String(form.responsible_name || "").trim().split(/\s+/)[0] || "";
  const digits = String(form.responsible_phone || "").replace(/\D/g, "");
  const phone = digits.length >= 12 ? digits : digits.length >= 10 ? `55${digits}` : "";
  const waText = `Olá${first ? `, ${first}` : ""}! Aqui está o link para assinar o contrato da Simpler: ${done?.signUrl || ""}`;
  const waUrl = `https://wa.me/${phone}?text=${encodeURIComponent(waText)}`;
  const inst = parseInt(form.setup_installments || "1", 10);
  const fmtDate = (d) => (d ? String(d).split("-").reverse().join("/") : "");

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(done.signUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* ignore */ }
  };

  const rows = [
    ["Cliente", form.customer_name],
    ["CNPJ", form.customer_cnpj],
    ["Responsável", form.responsible_name],
    ["E-mail", form.responsible_email],
    ["Mensalidade", form.monthly_fee ? `R$ ${form.monthly_fee}` : "—"],
    ["Adesão", form.setup_fee ? `R$ ${form.setup_fee}${inst > 1 ? ` em ${inst}x` : ""}` : "Sem adesão"],
    ["Vencimento", form.due_day ? `Dia ${form.due_day}` : "—"],
    ["1ª mensalidade", form.first_monthly_date ? fmtDate(form.first_monthly_date) : "30 dias após a assinatura"],
    ["Pagamento", form.payment_method || "—"],
    ["Consultor", consultantName || "—"],
  ];

  const steps = [
    { t: "Contrato enviado", d: "Link de assinatura enviado por e-mail.", ok: sent },
    { t: "Cliente assina", d: "Você recebe um aviso quando ele assinar." },
    { t: "Acesso liberado", d: "Portal criado e e-mail de boas-vindas com o link para criar a senha." },
    { t: "Cobranças", d: inst > 1 ? `Adesão em ${inst}x e mensalidade programada.` : "Adesão e mensalidade programadas." },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#1b1464] via-[#3423b8] to-[#5b3bf0] px-4 py-10">
      <main className="mx-auto w-full max-w-3xl">
        <div className="overflow-hidden rounded-[32px] bg-white shadow-2xl">
          <div className="border-b border-slate-100 px-7 py-8 text-center sm:px-10">
            <div className={`mx-auto flex h-16 w-16 items-center justify-center rounded-full text-3xl font-bold ${sent ? "bg-emerald-100 text-emerald-600" : "bg-[#ede9ff] text-[#5b3bf0]"}`}>
              {sent ? "✓" : "✎"}
            </div>
            <h1 className="mt-5 text-[32px] font-extrabold tracking-[-0.03em] text-[#1b1464]">
              {sent ? "Contrato enviado!" : "Rascunho salvo"}
            </h1>
            <p className="mx-auto mt-2 max-w-xl text-[15px] leading-relaxed text-slate-500">
              {sent ? (
                <>
                  <strong className="text-[#1b1464]">{form.responsible_name}</strong> recebeu o link de assinatura em{" "}
                  <strong className="text-[#1b1464]">{form.responsible_email}</strong>.
                </>
              ) : (
                "O contrato ficou salvo como rascunho. Você pode revisar e enviar quando quiser pela lista de contratos."
              )}
            </p>
          </div>

          {sent && done?.signUrl && (
            <div className="grid gap-3 border-b border-slate-100 px-7 py-6 sm:grid-cols-2 sm:px-10">
              <a href={waUrl} target="_blank" rel="noreferrer"
                className="flex items-center justify-center gap-2 rounded-2xl bg-[#25D366] px-5 py-3.5 text-[15px] font-bold text-white transition hover:brightness-95">
                <svg viewBox="0 0 24 24" className="h-5 w-5 fill-current" aria-hidden="true"><path d="M12 2a10 10 0 0 0-8.6 15.1L2 22l5-1.3A10 10 0 1 0 12 2Zm0 18.2c-1.5 0-3-.4-4.3-1.2l-.3-.2-3 .8.8-2.9-.2-.3A8.2 8.2 0 1 1 12 20.2Zm4.5-6.1c-.2-.1-1.5-.7-1.7-.8-.2-.1-.4-.1-.6.1l-.8 1c-.1.2-.3.2-.5.1a6.7 6.7 0 0 1-3.3-2.9c-.2-.4.2-.4.7-1.3.1-.2 0-.3 0-.4l-.8-1.8c-.2-.5-.4-.4-.6-.4h-.5c-.2 0-.4.1-.7.3-.2.3-.9.9-.9 2.2s.9 2.5 1 2.7c.1.2 1.8 2.8 4.4 3.9 1.6.7 2.3.8 3.1.6.5-.1 1.5-.6 1.7-1.2.2-.6.2-1.1.1-1.2l-.6-.3Z"/></svg>
                {phone ? "Enviar pelo WhatsApp" : "Enviar pelo WhatsApp (escolher contato)"}
              </a>
              <button type="button" onClick={copy}
                className="rounded-2xl border border-[#5b3bf0]/25 bg-white px-5 py-3.5 text-[15px] font-bold text-[#5b3bf0] transition hover:bg-[#f4f1ff]">
                {copied ? "Link copiado ✓" : "Copiar link de assinatura"}
              </button>
            </div>
          )}

          <div className="grid gap-8 px-7 py-7 sm:grid-cols-[1.15fr_1fr] sm:px-10">
            <div>
              <div className="text-[12px] font-bold uppercase tracking-[0.18em] text-[#5b3bf0]">Resumo</div>
              <dl className="mt-3 divide-y divide-slate-100 rounded-2xl border border-slate-100">
                {rows.map(([k, v]) => (
                  <div key={k} className="flex items-start justify-between gap-4 px-4 py-2.5">
                    <dt className="shrink-0 text-[13px] text-slate-500">{k}</dt>
                    <dd className="text-right text-[14px] font-semibold text-slate-800 break-words">{v || "—"}</dd>
                  </div>
                ))}
              </dl>
            </div>
            <div>
              <div className="text-[12px] font-bold uppercase tracking-[0.18em] text-[#5b3bf0]">Próximos passos</div>
              <ol className="mt-3 space-y-0">
                {steps.map((st, i) => (
                  <li key={st.t} className="relative flex gap-3 pb-5 last:pb-0">
                    {i < steps.length - 1 && <span className="absolute left-[15px] top-8 h-[calc(100%-24px)] w-px bg-slate-200" />}
                    <span className={`relative z-10 flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[13px] font-bold ${st.ok ? "bg-emerald-500 text-white" : "bg-[#ede9ff] text-[#5b3bf0]"}`}>
                      {st.ok ? "✓" : i + 1}
                    </span>
                    <div className="pt-1">
                      <div className="text-[14px] font-bold text-[#1b1464]">{st.t}</div>
                      <div className="text-[13px] leading-snug text-slate-500">{st.d}</div>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          </div>

          <div className="flex flex-col gap-3 border-t border-slate-100 bg-slate-50/60 px-7 py-5 sm:flex-row sm:px-10">
            <button type="button" onClick={() => nav("/commercial-portal")}
              className="flex-1 rounded-2xl border border-slate-200 bg-white px-5 py-3 text-[14px] font-bold text-slate-600 hover:bg-slate-50">
              Ir para meus contratos
            </button>
            <button type="button" onClick={() => window.location.assign("/commercial-portal/novo-contrato")}
              className="flex-1 rounded-2xl bg-[#1b1464] px-5 py-3 text-[14px] font-bold text-white hover:bg-[#2a1f8f]">
              Novo contrato
            </button>
          </div>
        </div>
      </main>
    </div>
  );
}

export default function CommercialNewContract() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const editId = params.get("id");

  const auth = useAuth() || {};
  const user = auth.user || {};
  const role = user.role || user.user?.role || "";
  const isAdmin = role === "commercial_master" || role === "platform_owner";

  const [step, setStep] = useState(1);
  const [form, setForm] = useState(() => {
    if (editId) return EMPTY;
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      return raw ? { ...EMPTY, ...JSON.parse(raw) } : EMPTY;
    } catch {
      return EMPTY;
    }
  });
  const [consultants, setConsultants] = useState([]);
  const [cnpjInfo, setCnpjInfo] = useState(null);
  const [lookupBusy, setLookupBusy] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(null);
  const [locked, setLocked] = useState(false);

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  useEffect(() => {
    if (editId) return;
    try {
      localStorage.setItem(DRAFT_KEY, JSON.stringify(form));
    } catch {
      // armazenamento indisponível: segue sem rascunho local
    }
  }, [form, editId]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [step, done]);

  useEffect(() => {
    if (!isAdmin) return;
    api
      .get("/commercial-portal/consultants")
      .then(({ data }) => {
        const rows = Array.isArray(data) ? data : data?.items || data?.consultants || [];
        setConsultants(rows);
      })
      .catch(() => {});
  }, [isAdmin]);

  useEffect(() => {
    if (!editId) return;
    api
      .get("/commercial-portal/contracts")
      .then(({ data }) => {
        const rows = Array.isArray(data) ? data : data?.items || data?.contracts || [];
        const c = rows.find((r) => String(r.id) === String(editId));
        if (!c) {
          setError("Contrato não encontrado.");
          return;
        }
        if (c.status && c.status !== "draft") {
          setLocked(true);
          setError("Este contrato já foi enviado para assinatura e não pode mais ser editado.");
        }
        const base = { ...EMPTY };
        Object.keys(EMPTY).forEach((k) => {
          if (c[k] !== undefined && c[k] !== null) base[k] = String(c[k]);
        });
        setForm({
          ...base,
          customer_cnpj: fmtCnpj(c.customer_cnpj),
          setup_fee: centsToInput(c.setup_fee_cents),
          monthly_fee: centsToInput(c.monthly_fee_cents),
          start_date: c.start_date ? String(c.start_date).slice(0, 10) : "",
          due_day: c.due_day ? String(c.due_day) : "10",
          setup_installments: c.setup_installments ? String(c.setup_installments) : "1",
          first_monthly_date: c.first_monthly_date || "",
          companies_limit: c.companies_limit ? String(c.companies_limit) : "",
        });
      })
      .catch(() => setError("Não foi possível carregar o contrato."));
  }, [editId]);

  const lookup = async (digits) => {
    const d = digits || onlyDigits(form.customer_cnpj);
    if (d.length !== 14) {
      setError("Digite um CNPJ com 14 dígitos.");
      return;
    }
    setLookupBusy(true);
    setError("");
    try {
      const { data } = await api.get(`/commercial-portal/cnpj/${d}`, { timeout: 25000 });
      if (!data || data.found === false) {
        setCnpjInfo(null);
        setError((data && data.message) || "Não foi possível consultar o CNPJ agora. Preencha manualmente.");
        return;
      }
      setCnpjInfo(data);
      setForm((f) => ({
        ...f,
        customer_cnpj: data.cnpj || f.customer_cnpj,
        customer_name: data.razao_social || f.customer_name,
        customer_address: data.endereco || f.customer_address,
        responsible_name: f.responsible_name || (data.socios && data.socios[0]) || "",
      }));
    } catch (err) {
      const det = err.response?.data?.detail;
      setError(
        typeof det === "string" && det.length < 200
          ? det
          : "Não foi possível consultar o CNPJ agora. Preencha a razão social e o endereço manualmente."
      );
    } finally {
      setLookupBusy(false);
    }
  };

  const onCnpjChange = (value) => {
    const masked = fmtCnpj(value);
    set("customer_cnpj", masked);
    const d = onlyDigits(masked);
    if (d.length === 14 && (!cnpjInfo || onlyDigits(cnpjInfo.cnpj) !== d)) {
      lookup(d);
    }
  };

  const validate = (s) => {
    if (s === 1) {
      if (onlyDigits(form.customer_cnpj).length !== 14) return "Informe um CNPJ válido.";
      if (form.customer_name.trim().length < 2) return "Informe a razão social.";
      if (!form.customer_address.trim()) return "Informe o endereço da empresa.";
    }
    if (s === 2) {
      if (form.responsible_name.trim().length < 3) return "Informe o nome do responsável legal (quem assina o contrato).";
      if (!isEmail(form.responsible_email)) return "Informe um e-mail válido para o responsável legal. É para ele que vai o link de assinatura.";
      if (form.accounting_contact_email && !isEmail(form.accounting_contact_email)) return "O e-mail do responsável pela contabilidade é inválido.";
      if (form.fiscal_contact_email && !isEmail(form.fiscal_contact_email)) return "O e-mail do responsável pelo fiscal é inválido.";
    }
    if (s === 3) {
      if (!(parseInt(form.companies_limit, 10) > 0)) return "Informe a quantidade de CNPJs contratada.";
      if (toCents(form.monthly_fee) <= 0) return "Informe o valor da mensalidade.";
      if (!form.start_date) return "Informe a data de início da vigência.";
      const dd = parseInt(form.due_day, 10);
      if (!(dd >= 1 && dd <= 31)) return "O dia de vencimento deve estar entre 1 e 31.";
      if (!form.payment_method) return "Escolha a forma de pagamento.";
      if (form.first_monthly_date) {
        const fd = new Date(form.first_monthly_date + "T12:00:00");
        const today0 = new Date(); today0.setHours(0, 0, 0, 0);
        if (isNaN(fd.getTime()) || fd <= today0) return "A data da primeira mensalidade precisa ser uma data futura.";
        if (fd.getDate() !== parseInt(form.due_day, 10)) return `A primeira mensalidade precisa cair no dia de vencimento escolhido (dia ${form.due_day}).`;
      }
      if (isAdmin && !form.consultant_id) return "Selecione o consultor responsável pela venda.";
    }
    return "";
  };

  const next = () => {
    const msg = validate(step);
    if (msg) {
      setError(msg);
      return;
    }
    setError("");
    setStep((s) => Math.min(4, s + 1));
  };

  const back = () => {
    setError("");
    setStep((s) => Math.max(1, s - 1));
  };

  const goTo = (s) => {
    if (s < step) {
      setError("");
      setStep(s);
    }
  };

  const payload = () => {
    const limit = parseInt(form.companies_limit, 10);
    return {
      customer_name: form.customer_name.trim(),
      customer_cnpj: fmtCnpj(form.customer_cnpj),
      customer_address: form.customer_address.trim() || null,
      responsible_name: form.responsible_name.trim(),
      responsible_email: form.responsible_email.trim().toLowerCase(),
      responsible_phone: form.responsible_phone || null,
      accounting_contact_name: form.accounting_contact_name.trim() || null,
      accounting_contact_email: form.accounting_contact_email.trim().toLowerCase() || null,
      accounting_contact_phone: form.accounting_contact_phone || null,
      fiscal_contact_name: form.fiscal_contact_name.trim() || null,
      fiscal_contact_email: form.fiscal_contact_email.trim().toLowerCase() || null,
      fiscal_contact_phone: form.fiscal_contact_phone || null,
      plan_name: `Plano ${limit} CNPJs`,
      companies_limit: limit,
      setup_fee_cents: toCents(form.setup_fee),
      monthly_fee_cents: toCents(form.monthly_fee),
      discount_cents: 0,
      start_date: form.start_date || null,
      due_day: parseInt(form.due_day, 10),
      setup_installments: parseInt(form.setup_installments || "1", 10),
      first_monthly_date: form.first_monthly_date || null,
      payment_method: form.payment_method,
      notes: form.notes.trim() || null,
      ...(isAdmin ? { consultant_id: form.consultant_id } : {}),
    };
  };

  const readError = (err, fallback) => {
    const d = err.response?.data?.detail;
    if (typeof d === "string") return d;
    if (Array.isArray(d)) return "Verifique os campos: " + d.map((x) => (x.loc || []).slice(-1)[0]).join(", ");
    if (d?.message) return d.message + (d.missing ? ` (${d.missing.join(", ")})` : "");
    return fallback;
  };

  const finish = async (send) => {
    for (const s of [1, 2, 3]) {
      const msg = validate(s);
      if (msg) {
        setStep(s);
        setError(msg);
        return;
      }
    }
    setBusy(true);
    setError("");
    let id = editId;
    try {
      if (id) {
        await api.patch(`/commercial-portal/contracts/${id}`, payload());
      } else {
        const { data } = await api.post("/commercial-portal/contracts", payload());
        id = data.id;
        try {
          localStorage.removeItem(DRAFT_KEY);
        } catch {
          // ignora
        }
        nav(`/commercial-portal/novo-contrato?id=${id}`, { replace: true });
      }
    } catch (err) {
      setError(readError(err, "Não foi possível salvar o contrato."));
      setBusy(false);
      return;
    }

    if (!send) {
      setDone({ id, sent: false });
      setBusy(false);
      return;
    }

    try {
      const { data } = await api.post(`/commercial-portal/contracts/${id}/zapsign/send`);
      setDone({ id, sent: true, signUrl: data?.sign_url });
    } catch (err) {
      setError(
        readError(err, "O contrato foi salvo como rascunho, mas não foi possível enviar para assinatura.") +
          " O rascunho está salvo; você pode tentar enviar de novo."
      );
    } finally {
      setBusy(false);
    }
  };

  const consultantName =
    (consultants.find((c) => String(c.id) === String(form.consultant_id)) || {}).name ||
    (isAdmin ? "" : user.name || "Você");

  // ------------------------------------------------------------
  // TELA DE CONCLUSÃO
  // ------------------------------------------------------------
  if (done) {
    return <ContractDoneScreen done={done} form={form} consultantName={consultantName} nav={nav} />;
  }

  // ------------------------------------------------------------
  // ASSISTENTE
  // ------------------------------------------------------------
  const current = STEPS[step - 1];

  return (
    <div className="min-h-screen bg-[linear-gradient(160deg,#120d52_0%,#22178a_38%,#3a25c2_72%,#5b3bf0_100%)] bg-fixed">
      <header className="sticky top-0 z-10 border-b border-white/10 bg-[#120d52]/40 backdrop-blur">
        <div className="mx-auto flex max-w-4xl items-center justify-between px-6 py-4">
          <button
            type="button"
            onClick={() => nav("/commercial-portal")}
            className="text-sm font-semibold text-white/80 transition-colors hover:text-white"
          >
            ← Voltar ao portal
          </button>
          <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-white/80">
            Simpler · {editId ? "Editar contrato" : "Novo contrato"}
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-6 py-10">
        {/* Etapas */}
        <ol className="grid grid-cols-4 gap-3">
          {STEPS.map((s) => {
            const state = s.id < step ? "done" : s.id === step ? "current" : "todo";
            return (
              <li key={s.id}>
                <button
                  type="button"
                  onClick={() => goTo(s.id)}
                  disabled={s.id >= step}
                  className="w-full text-left disabled:cursor-default"
                >
                  <div
                    className={
                      "h-1.5 rounded-full transition-all " +
                      (state === "todo" ? "bg-white/20" : "bg-white")
                    }
                  />
                  <div className="mt-3 flex items-center gap-2">
                    <span
                      className={
                        "flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[11px] font-bold " +
                        (state === "done"
                          ? "bg-white text-[#5b3bf0]"
                          : state === "current"
                          ? "bg-[#8b6dff] text-white ring-4 ring-white/20"
                          : "bg-white/15 text-white/70")
                      }
                    >
                      {state === "done" ? "✓" : s.id}
                    </span>
                    <span
                      className={
                        "text-sm font-bold " + (state === "todo" ? "text-white/55" : "text-white")
                      }
                    >
                      {s.title}
                    </span>
                  </div>
                  <div className="mt-0.5 hidden pl-8 text-[13px] text-white/60 sm:block">{s.hint}</div>
                </button>
              </li>
            );
          })}
        </ol>

        <section className="mt-8 rounded-[32px] border border-slate-200/80 bg-white p-6 shadow-[0_20px_60px_-30px_rgba(16,22,82,0.25)] sm:p-10">
          <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#5b3bf0]">
            Etapa {step} de 4
          </div>
          <h1 className="mt-2 text-3xl font-bold tracking-[-0.03em] text-[#101652]">
            {step === 1 && "Qual empresa está contratando?"}
            {step === 2 && "Quem são os contatos do cliente?"}
            {step === 3 && "Quais são as condições do contrato?"}
            {step === 4 && "Revise antes de enviar"}
          </h1>
          <p className="mt-2 text-sm text-slate-500">
            {step === 1 && "Digite o CNPJ: buscamos a razão social e o endereço automaticamente."}
            {step === 2 && "O responsável legal recebe o contrato para assinar. Os demais contatos ficam salvos para a implantação."}
            {step === 3 && "Esses valores preenchem o Quadro Comercial do contrato."}
            {step === 4 && "Confira os dados. Você pode editar qualquer bloco antes de enviar."}
          </p>

          <div className="mt-8">
            {/* ETAPA 1 */}
            {step === 1 && (
              <div className="grid gap-5">
                <Field label="CNPJ" required>
                  <div className="flex gap-3">
                    <input
                      className={inputCls}
                      inputMode="numeric"
                      placeholder="00.000.000/0000-00"
                      value={form.customer_cnpj}
                      disabled={locked}
                      onChange={(e) => onCnpjChange(e.target.value)}
                      autoFocus
                    />
                    <button
                      type="button"
                      onClick={() => lookup()}
                      disabled={lookupBusy || locked}
                      className="shrink-0 rounded-2xl border border-[#5b3bf0]/25 bg-[#5b3bf0]/5 px-5 text-sm font-bold text-[#5b3bf0] transition-all hover:bg-[#5b3bf0]/10 disabled:opacity-50"
                    >
                      {lookupBusy ? "Buscando..." : "Buscar"}
                    </button>
                  </div>
                </Field>

                {cnpjInfo?.situacao && (
                  <div
                    className={
                      "rounded-2xl px-4 py-3 text-[13px] font-semibold " +
                      (String(cnpjInfo.situacao).toUpperCase() === "ATIVA"
                        ? "bg-emerald-50 text-emerald-700"
                        : "bg-amber-50 text-amber-700")
                    }
                  >
                    Situação cadastral na Receita: {cnpjInfo.situacao}
                    {cnpjInfo.nome_fantasia ? ` · Nome fantasia: ${cnpjInfo.nome_fantasia}` : ""}
                  </div>
                )}

                <Field label="Razão social" required>
                  <input
                    className={inputCls}
                    value={form.customer_name}
                    disabled={locked}
                    onChange={(e) => set("customer_name", e.target.value)}
                  />
                </Field>

                <Field label="Endereço" required hint="Confira se o endereço da Receita está correto.">
                  <textarea
                    rows={2}
                    className={inputCls}
                    value={form.customer_address}
                    disabled={locked}
                    onChange={(e) => set("customer_address", e.target.value)}
                  />
                </Field>
              </div>
            )}

            {/* ETAPA 2 */}
            {step === 2 && (
              <div className="grid gap-5">
                <Section
                  title="Responsável legal"
                  description="Sócio ou administrador. É quem recebe e assina o contrato."
                >
                  <Field label="Nome" required>
                    <input className={inputCls} value={form.responsible_name} disabled={locked}
                      onChange={(e) => set("responsible_name", e.target.value)} />
                  </Field>
                  <Field label="E-mail" required>
                    <input className={inputCls} type="email" value={form.responsible_email} disabled={locked}
                      onChange={(e) => set("responsible_email", e.target.value)} />
                  </Field>
                  <Field label="Telefone / WhatsApp">
                    <input className={inputCls} inputMode="tel" value={form.responsible_phone} disabled={locked}
                      onChange={(e) => set("responsible_phone", fmtPhone(e.target.value))} />
                  </Field>
                </Section>

                {cnpjInfo?.socios?.length > 0 && (
                  <div className="-mt-2 flex flex-wrap items-center gap-2 text-[13px] text-slate-500">
                    Sócios na Receita:
                    {cnpjInfo.socios.slice(0, 5).map((s) => (
                      <button key={s} type="button" disabled={locked}
                        onClick={() => set("responsible_name", s)}
                        className="rounded-full border border-slate-200 bg-white px-3 py-1 font-semibold text-[#101652] hover:border-[#5b3bf0]/40">
                        {s}
                      </button>
                    ))}
                  </div>
                )}

                <Section title="Responsável pela contabilidade" description="Contato para a implantação e o dia a dia.">
                  <Field label="Nome">
                    <input className={inputCls} value={form.accounting_contact_name} disabled={locked}
                      onChange={(e) => set("accounting_contact_name", e.target.value)} />
                  </Field>
                  <Field label="E-mail">
                    <input className={inputCls} type="email" value={form.accounting_contact_email} disabled={locked}
                      onChange={(e) => set("accounting_contact_email", e.target.value)} />
                  </Field>
                  <Field label="Telefone / WhatsApp">
                    <input className={inputCls} inputMode="tel" value={form.accounting_contact_phone} disabled={locked}
                      onChange={(e) => set("accounting_contact_phone", fmtPhone(e.target.value))} />
                  </Field>
                </Section>

                <Section title="Responsável pelo fiscal" description="Quem acompanha as obrigações fiscais no escritório.">
                  <Field label="Nome">
                    <input className={inputCls} value={form.fiscal_contact_name} disabled={locked}
                      onChange={(e) => set("fiscal_contact_name", e.target.value)} />
                  </Field>
                  <Field label="E-mail">
                    <input className={inputCls} type="email" value={form.fiscal_contact_email} disabled={locked}
                      onChange={(e) => set("fiscal_contact_email", e.target.value)} />
                  </Field>
                  <Field label="Telefone / WhatsApp">
                    <input className={inputCls} inputMode="tel" value={form.fiscal_contact_phone} disabled={locked}
                      onChange={(e) => set("fiscal_contact_phone", fmtPhone(e.target.value))} />
                  </Field>
                </Section>
              </div>
            )}

            {/* ETAPA 3 */}
            {step === 3 && (
              <div className="grid gap-5 md:grid-cols-2">
                <Field label="Quantidade de CNPJs" required>
                  <input className={inputCls} inputMode="numeric" placeholder="Ex.: 80" value={form.companies_limit} disabled={locked}
                    onChange={(e) => set("companies_limit", onlyDigits(e.target.value).slice(0, 5))} />
                </Field>

                <Field label="Mensalidade (R$)" required>
                  <input className={inputCls} inputMode="numeric" placeholder="0,00" value={form.monthly_fee} disabled={locked}
                    onChange={(e) => set("monthly_fee", fmtMoney(e.target.value))} />
                </Field>

                <Field label="Adesão / setup (R$)" hint="Deixe em branco se não houver adesão.">
                  <input className={inputCls} inputMode="numeric" placeholder="0,00" value={form.setup_fee} disabled={locked}
                    onChange={(e) => set("setup_fee", fmtMoney(e.target.value))} />
                </Field>

                <Field label="Início da vigência" required>
                  <input className={inputCls} type="date" value={form.start_date} disabled={locked}
                    onChange={(e) => set("start_date", e.target.value)} />
                </Field>

                <Field label="Dia de vencimento" required>
                  <select className={inputCls} value={form.due_day} disabled={locked}
                    onChange={(e) => set("due_day", e.target.value)}>
                    {Array.from({ length: 31 }, (_, i) => String(i + 1)).map((d) => (
                      <option key={d} value={d}>Dia {d}</option>
                    ))}
                  </select>
                </Field>

                <Field label="Forma de pagamento" required>
                  <select className={inputCls} value={form.payment_method} disabled={locked}
                    onChange={(e) => set("payment_method", e.target.value)}>
                    {PAYMENT_METHODS.map((m) => (
                      <option key={m} value={m}>{m}</option>
                    ))}
                  </select>
                </Field>

                <Field label="Consultor responsável" required={isAdmin} className="md:col-span-2">
                  {isAdmin ? (
                    <select className={inputCls} value={form.consultant_id} disabled={locked}
                      onChange={(e) => set("consultant_id", e.target.value)}>
                      <option value="">Selecione</option>
                      {consultants.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name || c.email}
                          {c.email ? ` · ${c.email}` : ""}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-[#101652]">
                      {user.name || "Você"} <span className="text-slate-400">(vinculado automaticamente)</span>
                    </div>
                  )}
                </Field>

                <Field label="Observações internas" className="md:col-span-2" hint="Não aparece no contrato.">
                  <textarea rows={2} className={inputCls} value={form.notes} disabled={locked}
                    onChange={(e) => set("notes", e.target.value)} />
                </Field>
              </div>
            )}

            {/* ETAPA 4 */}
            {step === 3 && (
              <div className="mt-5 grid gap-4 sm:grid-cols-2">
                <label className="block">
                  <span className="mb-1.5 block text-[12px] font-bold uppercase tracking-[0.12em] text-slate-500">Parcelas da adesão</span>
                  <select className={inputCls} value={form.setup_installments || "1"} disabled={locked}
                    onChange={(e) => set("setup_installments", e.target.value)}>
                    {Array.from({ length: 12 }, (_, i) => String(i + 1)).map((n) => (
                      <option key={n} value={n}>{n === "1" ? "À vista (1x)" : `${n}x`}</option>
                    ))}
                  </select>
                  <span className="mt-1 block text-[12px] text-slate-400">
                    {(() => {
                      const t = toCents(form.setup_fee);
                      const n = parseInt(form.setup_installments || "1", 10);
                      if (!t || n <= 1) return "Vence 3 dias após a assinatura.";
                      return `${n}x de R$ ${(t / n / 100).toFixed(2).replace(".", ",")}: 1ª em 3 dias, as demais a cada mês.`;
                    })()}
                  </span>
                </label>
                <label className="block">
                  <span className="mb-1.5 block text-[12px] font-bold uppercase tracking-[0.12em] text-slate-500">Primeira mensalidade (opcional)</span>
                  <input type="date" className={inputCls} value={form.first_monthly_date || ""} disabled={locked}
                    onChange={(e) => set("first_monthly_date", e.target.value)} />
                  <span className="mt-1 block text-[12px] text-slate-400">
                    Em branco: primeiro dia {form.due_day} com pelo menos 30 dias após a assinatura.
                  </span>
                </label>
              </div>
            )}

            {step === 4 && (
              <div className="grid gap-4">
                <ReviewBlock
                  title="Empresa"
                  onEdit={() => goTo(1)}
                  rows={[
                    ["Razão social", form.customer_name],
                    ["CNPJ", form.customer_cnpj],
                    ["Endereço", form.customer_address],
                  ]}
                />
                <ReviewBlock
                  title="Contatos"
                  onEdit={() => goTo(2)}
                  rows={[
                    ["Responsável legal (assina)", form.responsible_name],
                    ["E-mail para assinatura", form.responsible_email],
                    ["Contabilidade", [form.accounting_contact_name, form.accounting_contact_email].filter(Boolean).join(" · ")],
                    ["Fiscal", [form.fiscal_contact_name, form.fiscal_contact_email].filter(Boolean).join(" · ")],
                  ]}
                />
                <ReviewBlock
                  title="Condições"
                  onEdit={() => goTo(3)}
                  rows={[
                    ["Quantidade de CNPJs", form.companies_limit && `${form.companies_limit} CNPJs ativos`],
                    ["Mensalidade", form.monthly_fee && `R$ ${form.monthly_fee}`],
                    ["Adesão / setup", form.setup_fee ? `R$ ${form.setup_fee}${parseInt(form.setup_installments || "1", 10) > 1 ? ` em ${form.setup_installments}x` : ""}` : "Sem adesão"],
                    ["Início da vigência", fmtDate(form.start_date)],
                    ["Vencimento", form.due_day && `Dia ${form.due_day} de cada mês`],
                    ["Primeira mensalidade", form.first_monthly_date ? form.first_monthly_date.split("-").reverse().join("/") : "Automática (30 dias após a assinatura)"],
                    ["Forma de pagamento", form.payment_method],
                    ["Consultor", consultantName],
                  ]}
                />
                <div className="rounded-2xl bg-[#5b3bf0]/5 px-5 py-4 text-sm text-[#101652]">
                  Ao enviar, <strong>{form.responsible_name || "o responsável legal"}</strong> recebe o contrato para
                  assinatura eletrônica em <strong>{form.responsible_email || "—"}</strong>, enviado pela ZapSign em nome
                  da Simpler.
                </div>
              </div>
            )}
          </div>

          {error && (
            <div className="mt-6 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
              {error}
            </div>
          )}

          {/* Navegação */}
          <div className="mt-8 flex flex-col-reverse gap-3 border-t border-slate-100 pt-6 sm:flex-row sm:items-center sm:justify-between">
            <button
              type="button"
              onClick={step === 1 ? () => nav("/commercial-portal") : back}
              className="rounded-2xl px-5 py-3 text-sm font-bold text-slate-500 hover:text-[#101652]"
            >
              {step === 1 ? "Cancelar" : "← Voltar"}
            </button>

            {step < 4 ? (
              <button
                type="button"
                onClick={next}
                disabled={lookupBusy}
                className="rounded-2xl bg-[#101652] px-8 py-3 text-sm font-bold text-white transition-all hover:opacity-90 disabled:opacity-50"
              >
                Avançar →
              </button>
            ) : (
              <div className="flex flex-col gap-3 sm:flex-row">
                <button
                  type="button"
                  onClick={() => finish(false)}
                  disabled={busy || locked}
                  className="rounded-2xl border border-slate-200 bg-white px-6 py-3 text-sm font-bold text-slate-600 hover:bg-slate-50 disabled:opacity-50"
                >
                  Salvar rascunho
                </button>
                <button
                  type="button"
                  onClick={() => finish(true)}
                  disabled={busy || locked}
                  className="rounded-2xl bg-[#5b3bf0] px-8 py-3 text-sm font-bold text-white shadow-[0_12px_30px_-12px_rgba(91,59,240,0.7)] transition-all hover:opacity-90 disabled:opacity-50"
                >
                  {busy ? "Enviando..." : "Enviar para assinatura"}
                </button>
              </div>
            )}
          </div>
        </section>

        <p className="mt-6 text-center text-[13px] text-white/60">
          {current.title} · o preenchimento fica salvo neste navegador até o envio.
        </p>
      </main>
    </div>
  );
}
